import React, { useEffect, useState } from 'react';
import { QrCode, ScanLine, TrendingUp, Home, Lock } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { scanApi } from '../api';

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-paelbi-line bg-white p-5 shadow-card">
    <div className="flex items-center gap-2 text-paelbi-brown"><Icon size={18} /><span className="text-xs font-semibold uppercase tracking-wide text-paelbi-ink/50">{label}</span></div>
    <p className="mt-3 font-display text-3xl text-paelbi-ink">{value}</p>
  </div>
);

export function ScanAnalytics() {
  const { isSuper, tenantId } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isSuper && !tenantId) return;
    setStats(null); setError(false);
    scanApi.stats(isSuper ? tenantId : undefined)
      .then(setStats)
      .catch(() => setError(true));
  }, [isSuper, tenantId]);

  const max = stats ? Math.max(1, ...stats.daily.map((d) => d.count)) : 1;

  return (
    <div data-testid="scan-analytics-page">
      <div className="flex items-center gap-2">
        <ScanLine size={24} className="text-paelbi-brown" />
        <h1 className="font-display text-3xl text-paelbi-ink">Scansioni QR</h1>
      </div>
      <p className="mt-1 text-paelbi-ink/60">Quanti ospiti aprono il tuo QR "scan-to-book". Ogni QR (struttura + accesso ospiti) punta alla tua pagina pubblica.</p>

      {error && <div className="mt-6 rounded-xl border border-paelbi-line bg-white p-6 text-paelbi-ink/60">Impossibile caricare le statistiche.</div>}

      {!stats && !error && <div className="mt-6 text-paelbi-ink/50">Caricamento…</div>}

      {stats && (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard icon={QrCode} label="Totale scansioni" value={stats.total} testid="scan-total" />
            <StatCard icon={TrendingUp} label="Ultimi 7 giorni" value={stats.last_7d} testid="scan-7d" />
            <StatCard icon={TrendingUp} label="Ultimi 30 giorni" value={stats.last_30d} testid="scan-30d" />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard icon={Home} label="QR Struttura" value={stats.by_source?.property ?? 0} testid="scan-property" />
            <StatCard icon={Lock} label="QR Accesso ospiti" value={stats.by_source?.guest ?? 0} testid="scan-guest" />
          </div>

          <div className="mt-6 rounded-2xl border border-paelbi-line bg-white p-6 shadow-card" data-testid="scan-chart">
            <h2 className="font-display text-xl text-paelbi-ink">Andamento (ultimi 14 giorni)</h2>
            <div className="mt-6 flex h-40 items-stretch gap-1.5">
              {stats.daily.map((d) => (
                <div key={d.day} className="group flex h-full flex-1 flex-col items-center justify-end">
                  <span className="mb-1 text-[10px] text-paelbi-ink/50 opacity-0 transition-opacity group-hover:opacity-100">{d.count}</span>
                  <div
                    className="w-full rounded-t bg-paelbi-brown/80 transition-all hover:bg-paelbi-brown"
                    style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? '4px' : '2px' }}
                    title={`${d.day}: ${d.count}`}
                  />
                  <span className="mt-1 text-[9px] text-paelbi-ink/40">{d.day.slice(8)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-paelbi-line bg-paelbi-bg p-5 text-sm text-paelbi-ink/70">
            💡 Stampa il QR dalla sezione <strong>Accesso ospiti</strong> e mettilo in camera o alla reception:
            ogni scansione porta l'ospite alla tua pagina, aumentando prenotazioni dirette ed esperienze.
          </div>
        </>
      )}
    </div>
  );
}

export default ScanAnalytics;
